import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Loader2, Newspaper } from "lucide-react";
import { Overline, Reveal } from "@/components/Reveal";
import { API } from "@/data/campaign";
import { isVideoUrl } from "@/utils/media";

const relTime = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? "s" : ""} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days > 1 ? "s" : ""} ago`;
};

const Thumb = ({ src, alt, className = "" }) =>
  src ? (
    <img src={src} alt={alt} loading="lazy" className={`aspect-video w-full object-cover ${className}`} />
  ) : (
    <div className={`flex aspect-video w-full items-center justify-center bg-pine-700 ${className}`}>
      <span className="font-serif text-3xl font-semibold italic text-paper/80">Ward 11</span>
    </div>
  );

const News = () => {
  const [news, setNews] = useState(null);
  const [updates, setUpdates] = useState([]);

  useEffect(() => {
    axios.get(`${API}/news`).then(({ data }) => setNews(data)).catch(() => setNews({ items: [], last_updated: "" }));
    axios.get(`${API}/news/campaign`).then(({ data }) => setUpdates(data.items)).catch(() => {});
  }, []);

  const items = news?.items || [];
  const [featured, ...rest] = items;

  return (
    <main data-testid="news-page" className="pt-28 lg:pt-32">
      {/* CAMPAIGN UPDATES — only shown when posts exist */}
      {updates.length > 0 && (
        <section data-testid="campaign-updates-section" className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <Overline>Campaign Updates</Overline>
            <h2 className="mt-4 font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl">
              News from the campaign — <span className="italic text-terra-500">posted by our team.</span>
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {updates.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <Link
                  to={`/news/campaign/${p.slug}`}
                  data-testid={`campaign-post-${p.slug}`}
                  className="group flex h-full flex-col border border-pine-700/15 bg-white/60 transition-colors hover:border-terra-500"
                >
                  {p.image_url && (
                    isVideoUrl(p.image_url) ? (
                      <div data-testid="campaign-post-video">
                        <video src={p.image_url} muted playsInline preload="metadata" className="block h-auto w-full object-contain" />
                      </div>
                    ) : (
                      <div className="overflow-hidden">
                        <img src={p.image_url} alt={p.title} loading="lazy" className="aspect-video w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                    )
                  )}
                  <div className="flex flex-1 flex-col p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra-500">
                      {new Date(p.published_at || p.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" })}
                    </p>
                    <h2 className="mt-2 font-serif text-xl font-semibold leading-snug tracking-tight group-hover:text-terra-500">
                      {p.title}
                    </h2>
                    {p.excerpt && <p className="mt-2 flex-1 text-sm leading-relaxed text-ink/70">{p.excerpt}</p>}
                    <span className="mt-4 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-pine-700 group-hover:text-terra-500">
                      Read more <ArrowUpRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* AUTO NEWS FEED */}
      <section data-testid="auto-news-section" className={`mx-auto max-w-7xl px-5 sm:px-8 ${updates.length ? "mt-24" : ""} pb-24`}>
        <Reveal>
          <Overline>Automatic Feed</Overline>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            Ward 11 <span className="italic text-terra-500">in the News</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg">
            Latest local stories about Streetsville, Meadowvale Village, Levi Creek, Credit Mills, Erin Mills, and
            Mississauga Ward 11.
          </p>
          {news?.last_updated && (
            <p data-testid="news-last-updated" className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-ink/50">
              Last updated {relTime(news.last_updated)}
            </p>
          )}
        </Reveal>

        {!news ? (
          <div className="flex items-center gap-2 py-20 text-ink/50">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading the latest stories…
          </div>
        ) : items.length === 0 ? (
          <div data-testid="news-empty-state" className="mt-12 flex flex-col items-center border border-dashed border-pine-700/25 py-20 text-center">
            <Newspaper className="h-8 w-8 text-ink/30" />
            <p className="mt-4 font-serif text-xl text-ink/60">No new Ward 11 stories right now. Check back soon.</p>
          </div>
        ) : (
          <>
            <Reveal className="mt-12">
              <a
                href={featured.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="news-featured"
                className="group grid overflow-hidden border border-pine-700/15 bg-white/60 transition-colors hover:border-terra-500 lg:grid-cols-2"
              >
                <div className="overflow-hidden">
                  <Thumb src={featured.image_url} alt={featured.title} className="transition-transform duration-700 group-hover:scale-105 lg:h-full" />
                </div>
                <div className="flex flex-col p-8 sm:p-10">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra-500">
                    {featured.source} · {relTime(featured.published_at)}
                  </p>
                  <h2 className="mt-3 font-serif text-2xl font-semibold leading-snug tracking-tight group-hover:text-terra-500 sm:text-3xl">
                    {featured.title}
                  </h2>
                  {featured.excerpt && <p className="mt-4 flex-1 text-base leading-relaxed text-ink/70">{featured.excerpt}</p>}
                  <span className="mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-pine-700 group-hover:text-terra-500">
                    Read full story <ArrowUpRight className="h-4 w-4" />
                  </span>
                </div>
              </a>
            </Reveal>

            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
              {rest.map((n, i) => (
                <Reveal key={n.id || n.url} delay={(i % 3) * 0.06}>
                  <a
                    href={n.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`news-card-${i}`}
                    className="group flex h-full flex-col border border-pine-700/15 bg-white/60 transition-colors hover:border-terra-500"
                  >
                    <div className="overflow-hidden">
                      <Thumb src={n.image_url} alt={n.title} className="transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-terra-500">
                        {n.source} · {relTime(n.published_at)}
                      </p>
                      <h3 className="mt-2 font-serif text-lg font-semibold leading-snug tracking-tight group-hover:text-terra-500">
                        {n.title}
                      </h3>
                      {n.excerpt && <p className="mt-2 flex-1 text-sm leading-relaxed text-ink/70">{n.excerpt}</p>}
                      <span className="mt-4 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-pine-700 group-hover:text-terra-500">
                        Read full story <ArrowUpRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          </>
        )}
      </section>
    </main>
  );
};

export default News;
